/**
 * HomePage Component - Fixed Desktop Scrolling
 * Proper scrollable sidebars and main content area
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Book, 
  AlertCircle,
  GraduationCap,
  Users,
  FileText,
  ArrowRight,
  UserPlus,
  MessageSquare, 
  User,
  Award,
  X,
  RefreshCw,
  Trophy
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import HomeSocialFeed from '../../social/components/HomeSocialFeed';
import SocialPage from '../../social/pages/SocialPage';
import challengeService from '../../academic/services/challengeService';
import { formatTimeRemaining } from '../../academic/utils/timeUtils';
import { toast } from 'react-hot-toast';
import { SuggestionsList } from '../../suggestions';
import MessageButton from '../../messaging/components/MessageButton';

// Clean academic links - keeping your existing structure
const academicQuickLinks = [
  {
    title: 'Assignments Help',
    icon: <Book className="w-6 h-6 text-red-500" />,
    path: '/assignments',
    color: 'red',
  },
  {
    title: 'Tutors',
    icon: <GraduationCap className="w-6 h-6 text-purple-500" />,
    path: '/tutors',
    color: 'purple',
  },
  {
    title: 'Daily Quiz',
    icon: <Users className="w-6 h-6 text-blue-500" />,
    path: '/academic/challenge',
    color: 'blue',
  },
  {
    title: 'Resources',
    icon: <FileText className="w-6 h-6 text-green-500" />,
    path: '/resources',
    color: 'green',
  }
];

const HomePage = () => {
  const { userProfile, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('feed');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayScore, setTodayScore] = useState(null);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [userTotalScore, setUserTotalScore] = useState(0);
  const [userRank, setUserRank] = useState(null);
  const [nextChallengeTime, setNextChallengeTime] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [refreshingLeaderboard, setRefreshingLeaderboard] = useState(false);

  // Fetch leaderboard data
  useEffect(() => {
    const fetchChallengeData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        const completed = await challengeService.hasCompletedTodaysChallenge(currentUser.uid);
        setHasCompletedToday(completed);
        
        const userScoreData = await challengeService.fetchUserScores(currentUser.uid);
        if (userScoreData) {
          setUserTotalScore(userScoreData.totalScore || 0);
          if (completed && userScoreData.stats?.bestScore) {
            setTodayScore(userScoreData.stats.bestScore);
          }
        }
        
        const leaderboardData = await challengeService.fetchLeaderboard(5);
        setLeaderboard(leaderboardData);
        
        const userRankPosition = await challengeService.getUserRankingPosition(currentUser.uid);
        setUserRank(userRankPosition);
        
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const timeUntilTomorrow = Math.floor((tomorrow - now) / 1000);
        setNextChallengeTime(timeUntilTomorrow);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching challenge data:', error);
        setLoading(false);
      }
    };

    fetchChallengeData();
  }, [currentUser]);

  // Get rank styling
  const getRankBadge = (rank) => {
    const colors = {
      1: 'bg-yellow-500 text-white',
      2: 'bg-gray-400 text-white', 
      3: 'bg-orange-600 text-white',
      default: 'bg-green-500 text-white'
    };
    return colors[rank] || colors.default;
  };

  const handleTakeChallengeClick = () => {
    if (hasCompletedToday) {
      toast.success("You've already completed today's challenge! Come back tomorrow for a new one.");
    }
  };

  const handleRefreshLeaderboard = async () => {
    if (!currentUser || refreshingLeaderboard) return;
    
    try {
      setRefreshingLeaderboard(true);
      const leaderboardData = await challengeService.fetchLeaderboard(5);
      setLeaderboard(leaderboardData);
      toast.success("Leaderboard refreshed");
    } catch (error) {
      console.error('Error refreshing leaderboard:', error);
      toast.error("Couldn't refresh leaderboard");
    } finally {
      setRefreshingLeaderboard(false);
    }
  };

  const handleViewProfile = (profile) => {
    setViewingProfile(profile);
    setShowProfileModal(true);
  };
  
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setViewingProfile(null);
  };

  // Clean leaderboard section
  const LeaderboardSection = () => (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-4">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center">
          <Trophy className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-800">Top Scholars</h2>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleRefreshLeaderboard}
            disabled={refreshingLeaderboard}
            className={`text-gray-500 hover:text-gray-700 p-1 ${refreshingLeaderboard ? 'animate-spin' : ''}`}
            aria-label="Refresh leaderboard"
          >
            <RefreshCw size={16} />
          </button>
          <Link to="/academic/leaderboard" className="text-sm text-green-500 flex items-center hover:text-green-600">
            View All <ChevronRight size={16} />
          </Link>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {/* Current User Score */}
        <Link 
          to="/academic/challenge" 
          className="flex items-center justify-between p-4 hover:bg-green-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              {userProfile?.photos && userProfile.photos.length > 0 ? (
                <img 
                  src={userProfile.photos[0]} 
                  alt="Your Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-600 rounded-full">
                  {userProfile?.basicInfo?.name?.charAt(0) || <User size={18} />}
                </div>
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">
                You {userRank && <span className="text-gray-500 text-sm">(#{userRank})</span>}
              </p>
              <p className="text-sm text-gray-500">
                @{userProfile?.basicInfo?.username || 'username'}
              </p>
            </div>
          </div>
          <div className="font-bold text-green-600">
            {userTotalScore} pts
          </div>
        </Link>

        {/* Top Users */}
        {loading ? (
          <div className="p-6 text-center">
            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading leaderboard...</p>
          </div>
        ) : (
          leaderboard.map((person) => (
            <div key={person.userId} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    {person.avatar ? (
                      <img 
                        src={person.avatar} 
                        alt={person.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 rounded-full">
                        {person.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className={`absolute -top-1 -right-1 w-5 h-5 ${getRankBadge(person.rank)} rounded-full flex items-center justify-center text-xs font-bold`}>
                    {person.rank}
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{person.name}</p>
                  <p className="text-sm text-gray-500">@{person.username || 'user'}</p>
                </div>
              </div>
              <div className="font-bold text-green-600">
                {person.totalScore} pts
              </div>
            </div>
          ))
        )}
      </div>

      {/* Challenge Button */}
      <div className="p-4 border-t border-gray-100">
        <Link 
          to="/academic/challenge" 
          className="inline-flex items-center justify-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors w-full"
          onClick={hasCompletedToday ? handleTakeChallengeClick : undefined}
        >
          <Award className="mr-2" size={18} />
          {hasCompletedToday ? "View Today's Results" : "Take Daily Challenge"}
        </Link>
        
        {hasCompletedToday && nextChallengeTime && (
          <div className="mt-2 text-center text-xs text-gray-500">
            Next challenge: {formatTimeRemaining(nextChallengeTime, false)}
          </div>
        )}
      </div>
    </div>
  );

  // Enhanced academic section with better cards
  const AcademicSection = () => (
    <div className="space-y-4">
      {/* Academic Quick Links Grid */}
      <div className="grid grid-cols-2 gap-3">
        {academicQuickLinks.map((link, index) => (
          <Link 
            key={index} 
            to={link.path}
            className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-200 hover:shadow-md transition-all group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-green-50 transition-colors">
                {link.icon}
              </div>
            </div>
            <div className="font-medium text-gray-800 group-hover:text-green-600 transition-colors">
              {link.title}
            </div>
          </Link>
        ))}
      </div>

      {/* Assignment Alert */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start">
        <div className="mr-3 mt-1">
          <AlertCircle className="w-5 h-5 text-yellow-500" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-gray-800">Get Assignment Help</h3>
          <p className="text-sm text-gray-600 mt-1">Our tutors are ready to assist you</p>
          <Link to="/assignments" className="text-sm font-medium text-green-500 hover:text-green-600 mt-2 inline-block">
            View Details →
          </Link>
        </div>
      </div>

      {/* Leaderboard Section */}
      <LeaderboardSection />
    </div>
  );

  // Desktop academic section - FIXED: Removed sticky and height constraints
  const DesktopAcademicSection = () => (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center">
          <GraduationCap className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-800">Academic</h2>
        </div>
        <Link to="/academic" className="text-sm text-green-500 flex items-center hover:text-green-600">
          View All <ChevronRight size={16} />
        </Link>
      </div>
      
      {/* Removed max-height and overflow constraints - let it flow naturally */}
      <div className="divide-y divide-gray-100">
        {academicQuickLinks.map((link, index) => (
          <Link 
            key={index} 
            to={link.path}
            className="p-4 flex items-center hover:bg-gray-50 transition-colors group"
          >
            <div className="p-2 bg-gray-50 rounded-lg mr-3 group-hover:bg-green-50 transition-colors">
              {link.icon}
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-800 group-hover:text-green-600 transition-colors">
                {link.title}
              </div>
            </div>
            <ChevronRight size={16} className="text-gray-400 group-hover:text-green-500 transition-colors" />
          </Link>
        ))}
        
        {/* Assignment help banner */}
        <div className="p-4 m-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-800 text-sm">Need Help?</h4>
              <p className="text-xs text-gray-600 mt-1">Get instant assignment assistance</p>
            </div>
            <Link 
              to="/assignments" 
              className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-medium rounded-lg transition-colors"
            >
              Help Me
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Layout - FIXED: Proper scrolling structure */}
      <div className="hidden lg:block">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-12 gap-6 mt-4">
            {/* Left Sidebar - FIXED: Removed sticky, allow natural scrolling */}
            <div className="col-span-3 space-y-4">
              <LeaderboardSection />
              <DesktopAcademicSection />
            </div>
            
            {/* Main Content - FIXED: Better height management */}
            <div className="col-span-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px]">
                <SocialPage />
              </div>
            </div>
            
            {/* Right Sidebar - FIXED: Removed sticky */}
            <div className="col-span-3 space-y-4">
              <SuggestionsList 
                limit={3}
                showViewAll={true}
                showHeader={true}
                title="Suggested Linkers"
                onProfileView={handleViewProfile}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Unchanged */}
      <div className="lg:hidden">
        <div className="container mx-auto px-4 mt-4">
          {/* Tab Navigation */}
          <div className="bg-white shadow-sm rounded-lg mb-4 overflow-hidden border border-gray-200">
            <div className="flex">
              <button 
                className={`flex-1 py-3 text-center font-medium text-sm transition-colors ${
                  activeTab === 'feed' 
                    ? 'text-green-600 bg-green-50 border-b-2 border-green-500' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('feed')}
              >
                Social Feed
              </button>
              <button 
                className={`flex-1 py-3 text-center font-medium text-sm transition-colors ${
                  activeTab === 'academic' 
                    ? 'text-green-600 bg-green-50 border-b-2 border-green-500' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('academic')}
              >
                Academic
              </button>
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="mb-4 pb-20">
            {activeTab === 'feed' ? (
              <div className="space-y-4">
                <HomeSocialFeed />
                <SuggestionsList 
                  limit={3}
                  showViewAll={true}
                  showHeader={true}
                  title="Suggested Linkers"
                  onProfileView={handleViewProfile}
                />
              </div>
            ) : (
              <AcademicSection />
            )}
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfileModal && viewingProfile && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={closeProfileModal}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-t-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:rounded-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Profile Details</h3>
                  <button 
                    onClick={closeProfileModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                  <div className="flex items-center">
                    <img 
                      src={viewingProfile.photos && viewingProfile.photos.length > 0 
                        ? viewingProfile.photos[0]
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(viewingProfile.basicInfo?.name || 'User')}&background=4CAF50&color=fff`} 
                      alt={viewingProfile.basicInfo?.name} 
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {viewingProfile.basicInfo?.name || 'User'}
                      </h2>
                      <p className="text-sm text-gray-500">
                        @{viewingProfile.basicInfo?.username || 'username'}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-md font-semibold text-gray-700 mb-2">Academic Information</h3>
                    <div className="grid grid-cols-2 gap-y-2 text-sm">
                      <div className="text-gray-500">University</div>
                      <div className="text-gray-700">{viewingProfile.academic?.university || 'N/A'}</div>
                      
                      <div className="text-gray-500">Degree</div>
                      <div className="text-gray-700">{viewingProfile.academic?.degree || 'N/A'}</div>
                      
                      <div className="text-gray-500">Year of Study</div>
                      <div className="text-gray-700">{viewingProfile.academic?.yearOfStudy || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 flex space-x-2">
                  <Link
                    to={`/connect?user=${viewingProfile.id}`}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-md py-2 text-sm font-medium flex items-center justify-center"
                  >
                    <UserPlus size={16} className="mr-2" /> Connect
                  </Link>
                  
                  <MessageButton
                    userId={viewingProfile.id}
                    variant="secondary"
                    className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md py-2 text-sm font-medium"
                    iconProps={{ size: 16, className: "mr-2" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;